import { useCallback, useMemo } from "react";
import {
  TechAvailabilityResponse,
  BrandDetails,
} from "../../../Interface/scheduler/form";
import { fetchTechAvailability } from "../../../service/apiService";
import {
  HOW_MANY_WEEKS_TO_DISPLAY,
  sharedStateKeys,
} from "../../../constants/constant";
import { useSharedStateWithActions } from "../../../common/hooks/useSharedState";
import { useJukPaymentScenario } from "./useJukPaymentScenario";
import { useFormContext } from "../../../common/contexts/FormContext";
import { useFormConfigContext } from "../../../common/contexts/FormConfigContext";

interface UseScheduleTechProps {
  brandDetails?: BrandDetails | null;
  setIsLoading?: (loading: boolean) => void;
  setScheduleCollection?: (collection: TechAvailabilityResponse[]) => void;
  setOnline?: (online: boolean) => void;
  setTechnicalModalOpen?: (open: boolean) => void;
}

interface UseScheduleTechResult {
  scheduleTech: (
    webLocationId: string,
    wdgServiceAccordionNonPriciableSkuId?: string
  ) => Promise<TechAvailabilityResponse[] | undefined>;
  isSchedulingDisabled: boolean;
  maxWeeksCap: number;
  fetchAvailabilityPeriod: (
    startDate: string,
    days: number,
    webLocationId: string,
    wdgServiceAccordionNonPriciableSkuId?: string
  ) => Promise<TechAvailabilityResponse[]>;
}

export const useScheduleTech = ({
  brandDetails,
  setIsLoading,
  setScheduleCollection,
  setOnline,
  setTechnicalModalOpen,
}: UseScheduleTechProps = {}): UseScheduleTechResult => {
  const { setValue, state } = useSharedStateWithActions();
  const handleJukPaymentScenario = useJukPaymentScenario();
  const { formValues, setFormValues } = useFormContext();
  const { formConfig } = useFormConfigContext();

  // Memoize computed values
  const maxWeeksCap = useMemo(() => {
    return Number(brandDetails?.calendarMaxWeeksCap) || 4;
  }, [brandDetails?.calendarMaxWeeksCap]);

  const isSchedulingDisabled = useMemo(() => {
    return (
      state[sharedStateKeys.IS_WARRANTY] ||
      formValues?.manufacturerWarranty === "yes"
    );
  }, [state, formValues?.manufacturerWarranty]);

  // Memoize date utility function
  const getCurrentDate = useCallback(() => {
    const date = new Date();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }, []);

  // Utility function for date calculation
  const getDateAfterDays = useCallback((days: number): string => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }, []);

  // Optimized function to determine initial weeks to fetch
  const getInitialWeeksToFetch = useCallback(
    (maxWeeks: number, calendarInWeeks: number): number => {
      return maxWeeks === 1
        ? 1
        : Math.min(calendarInWeeks || HOW_MANY_WEEKS_TO_DISPLAY, maxWeeks);
    },
    []
  );

  // Fetch availability for a specific period
  const fetchAvailabilityPeriod = useCallback(
    async (
      startDate: string,
      days: number,
      weblocationId: string,
      wdgServiceAccordionNonPriciableSkuId?: string
    ): Promise<TechAvailabilityResponse[]> => {
      try {
        const response = await fetchTechAvailability(
          weblocationId || formValues?.franchiseWebLocationId,
          startDate,
          days,
          formValues,
          formConfig,
          wdgServiceAccordionNonPriciableSkuId || ""
        );

        return response?.resultCollection || [];
      } catch (error) {
        console.error("Error fetching availability period:", error);
        return [];
      }
    },
    [formValues, formConfig]
  );

  // Handle the case when no slots are available
  const handleNoSlotsAvailable = useCallback(async () => {
    if (setOnline) {
      setOnline(false);
    }
    setValue(sharedStateKeys.IS_TECH_AVAILABLE_FAILS, true);

    try {
      await handleJukPaymentScenario({
        formValues,
        formConfig,
        setTechnicalModalOpen,
        setFormValues,
        bookingWrapper: brandDetails?.capture_lead_flag as boolean,
      });
    } catch (error) {
      console.error("Error handling JUK payment scenario:", error);
    }
  }, [
    setOnline,
    setValue,
    handleJukPaymentScenario,
    formValues,
    formConfig,
    setTechnicalModalOpen,
    setFormValues,
    brandDetails,
  ]);

  // Main scheduling function
  const scheduleTech = useCallback(
    async (
      webLocationId: string,
      wdgServiceAccordionNonPriciableSkuId = ""
    ): Promise<TechAvailabilityResponse[] | undefined> => {
      if (isSchedulingDisabled) {
        setIsLoading(false);
        return undefined;
      }

      let shouldUseNewFlow = false;
      let calendarInWeeks = HOW_MANY_WEEKS_TO_DISPLAY;
      if (Array.isArray(formConfig?.steps)) {
        for (const step of formConfig.steps) {
          if (
            step?.steptype === "request-time-section" &&
            step?.calendarNewFlow
          ) {
            // Update calendar settings from step
            setValue(sharedStateKeys.CALENDAR_IN_WEEKS, step.calendarInWeeks);
            calendarInWeeks =
              Number(step?.calendarInWeeks) || HOW_MANY_WEEKS_TO_DISPLAY;
            shouldUseNewFlow = true;
            break; // Only update for the first matching step
          }
        }
      }

      try {
        if (setIsLoading) {
          setIsLoading(true);
        }

        let allSlots: TechAvailabilityResponse[] = [];
        let hasAvailableSlots = false;
        // Calculate initial weeks based on current flow state
        const initialWeeksToFetch = shouldUseNewFlow
          ? getInitialWeeksToFetch(maxWeeksCap, calendarInWeeks)
          : maxWeeksCap;
        const remainingWeeksToFetch = maxWeeksCap - initialWeeksToFetch;
        setValue(sharedStateKeys.REMAINING_WEEKS, remainingWeeksToFetch);

        const initialSlots = await fetchAvailabilityPeriod(
          getCurrentDate(),
          initialWeeksToFetch * 7,
          webLocationId || formValues?.franchiseWebLocationId,
          wdgServiceAccordionNonPriciableSkuId
        );

        if (initialSlots.length > 0) {
          allSlots = [...initialSlots];
          hasAvailableSlots = initialSlots.some((slot) => slot.isAvailable);
        }
        // Final processing
        if (allSlots.length > 0 && hasAvailableSlots) {
          if (setScheduleCollection) {
            setScheduleCollection(allSlots);
          }
          setValue(sharedStateKeys.IS_TECH_AVAILABLE_FAILS, false);
          return allSlots;
        } else {
          await handleNoSlotsAvailable();
          return [];
        }
      } catch (error) {
        console.error("Error in scheduleTech:", error);
        setValue(sharedStateKeys.IS_TECH_AVAILABLE_FAILS, true);
        await handleNoSlotsAvailable();
        return undefined;
      } finally {
        if (setIsLoading) {
          setIsLoading(false);
        }
      }
    },
    [
      isSchedulingDisabled,
      formValues?.franchiseWebLocationId,
      setIsLoading,
      maxWeeksCap,
      getInitialWeeksToFetch,
      fetchAvailabilityPeriod,
      getCurrentDate,
      getDateAfterDays,
      setScheduleCollection,
      setValue,
      handleNoSlotsAvailable,
    ]
  );

  return {
    scheduleTech,
    isSchedulingDisabled,
    maxWeeksCap,
    fetchAvailabilityPeriod,
  };
};
